import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, Award, Briefcase, GraduationCap, Shield, Users, Target } from 'lucide-react';
import { motion } from 'framer-motion';

export const AboutPage = () => {
  const credentials = [
    { icon: Briefcase, title: 'Superintendent-Level Cabinet Experience', desc: 'Executive leadership at the highest levels of district administration' },
    { icon: Users, title: 'Turnaround Principal', desc: 'Led schools from underperformance to excellence with measurable results' },
    { icon: Target, title: 'District Curriculum Director', desc: 'Designed and implemented transformative instructional programs' },
    { icon: GraduationCap, title: 'CTE Director', desc: 'Created innovative career and technical education pathways' },
    { icon: Shield, title: 'Military Officer', desc: '21 years as Captain, Alabama Army National Guard & USMC veteran' },
    { icon: Award, title: 'National Speaker', desc: 'Keynote speaker on leadership, systems, and school transformation' },
  ];

  const achievements = [
    'Led struggling school to first "A" rating on state report card',
    'Contributed to #1 secondary school ranking in Madison County',
    'Executed full school turnaround within single academic year',
    'Created CTE immersion program with domestic and international experiences',
    'Served as adjunct instructor at Alabama A&M University',
    'Developed signature frameworks adopted by districts nationwide',
  ];

  return (
    <div className="pt-20 lg:pt-24" data-testid="about-page">
      {/* Hero Section */}
      <section className="section-padding bg-[#F7F8F6]" data-testid="about-hero">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="overline mb-4">About the Founder</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#172033] leading-tight mb-6">
                Captain<br />Cameron Whitlow
              </h1>
              <p className="text-slate-600 text-lg lg:text-xl leading-relaxed mb-8">
                Founder & Principal Consultant at Legacy Leadership Group. Over 25 years of educational leadership experience, combining military discipline with executive expertise to transform school districts.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="bg-[#0B1F3A] hover:bg-[#172033] text-white rounded-full px-8 py-4 font-body">
                    Schedule a Conversation
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/book">
                  <Button variant="outline" className="border-2 border-[#0B1F3A] text-[#0B1F3A] hover:bg-[#0B1F3A] hover:text-white rounded-full px-8 py-4 font-body">
                    Book for Speaking
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instructional-lead/artifacts/eyi4u393_83285.jpg"
                  alt="Captain Cameron Whitlow"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#4F8A2F] text-white p-6 rounded-2xl hidden md:block">
                <p className="font-display text-3xl font-bold">25+</p>
                <p className="text-sm">Years in Education</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="section-padding bg-[#0B1F3A]">
        <div className="container-premium">
          <div className="max-w-4xl mx-auto text-center">
            <p className="overline text-[#4F8A2F] mb-6">Our Mission</p>
            <blockquote className="font-display text-2xl sm:text-3xl lg:text-4xl text-white leading-relaxed mb-8">
              "To partner with school districts to strengthen leadership capacity, improve organizational systems, implement sustainable practices, and produce measurable results that continue long after the consulting engagement ends."
            </blockquote>
            <p className="text-[#4F8A2F] font-body font-semibold">— Cameron Whitlow</p>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="section-padding bg-white" data-testid="credentials-section">
        <div className="container-premium">
          <div className="max-w-2xl mb-16">
            <p className="overline mb-4">Experience & Expertise</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
              Leadership Credentials
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              A proven track record of transforming schools and developing leaders across multiple roles and contexts.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {credentials.map((cred, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-premium hover-lift"
                data-testid={`credential-${index}`}
              >
                <div className="w-12 h-12 bg-[#0B1F3A]/10 rounded-xl flex items-center justify-center mb-4">
                  <cred.icon className="w-6 h-6 text-[#0B1F3A]" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-xl font-bold text-[#172033] mb-2">{cred.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{cred.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="section-padding bg-[#F7F8F6]" data-testid="achievements-section">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="overline mb-4">Proven Results</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
                Measurable Impact
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Results-driven leadership that transforms schools and produces outcomes that matter.
              </p>
              <ul className="space-y-4">
                {achievements.map((achievement, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-[#4F8A2F] mt-1 flex-shrink-0" strokeWidth={2} />
                    <span className="text-slate-700">{achievement}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#0B1F3A] text-white p-8 rounded-2xl text-center">
                <p className="font-display text-4xl lg:text-5xl font-bold text-[#4F8A2F] mb-2">99%</p>
                <p className="text-sm text-slate-300">Graduation Rate</p>
              </div>
              <div className="bg-[#172033] text-white p-8 rounded-2xl text-center">
                <p className="font-display text-4xl lg:text-5xl font-bold text-[#4F8A2F] mb-2">99%</p>
                <p className="text-sm text-slate-300">CCRI Achievement</p>
              </div>
              <div className="bg-[#172033] text-white p-8 rounded-2xl text-center">
                <p className="font-display text-4xl lg:text-5xl font-bold text-[#4F8A2F] mb-2">21</p>
                <p className="text-sm text-slate-300">Years Military Service</p>
              </div>
              <div className="bg-[#0B1F3A] text-white p-8 rounded-2xl text-center">
                <p className="font-display text-4xl lg:text-5xl font-bold text-[#4F8A2F] mb-2">1st</p>
                <p className="text-sm text-slate-300">"A" Rating Achieved</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-premium text-center">
          <p className="overline mb-4">Let's Connect</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#172033] mb-6">
            Ready to Transform Your District?
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto mb-10">
            Schedule a strategy session to discuss how Legacy Leadership Group can partner with your district to build leadership, strengthen systems, and produce lasting results.
          </p>
          <Link to="/contact">
            <Button className="bg-[#0B1F3A] hover:bg-[#172033] text-white rounded-full px-10 py-5 font-body text-lg">
              Schedule a Strategy Session
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
